"use client";

// Notification bell — shown in the shell only while signed in. Polls the
// unread count every 45s, and opens a small popover (not a page) listing the
// most recent notifications. Clicking an item marks it read and, when it
// points at a job, navigates there; "Mark all as read" clears the badge.
//
// Positioning: the panel is `fixed`, anchored at click time to the bell
// button's bounding rect (viewport coords, clamped to stay on-screen). It must
// NOT be `absolute` inside the wrapper: in the sidebar the nav is an
// `overflow-y-auto` scroller, which clips absolutely-positioned descendants on
// both axes — and since the wrapper sits near the sidebar's left edge while
// the panel is wider than the 240px sidebar, the clipped result renders as a
// tall strip running down the nav. `fixed` escapes that clipping (no
// transformed ancestor establishes a containing block), and scroll/resize
// closes the panel so it can't drift away from its bell.
//
// Both axes are viewport-checked at open time. Horizontal: prefer
// left-aligned with the bell, flip to right-aligned near the viewport's right
// edge (mobile top bar). Vertical: open downward by default; when the space
// below the bell can't fit the worst-case panel but the space above is bigger,
// open upward — anchored by CSS `bottom` instead of `top`, so the panel's
// variable height always hugs the bell as it grows upward.

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  type Notification,
} from "@/app/lib/api";
import { useAuth } from "@/app/lib/auth-context";
import { formatRelativeTime } from "@/app/lib/relative-time";
import { BellIcon, InboxIcon, SpinnerIcon } from "@/app/components/icons";

const UNREAD_POLL_MS = 45_000;
const DROPDOWN_LIMIT = 10;

function NotificationItem({
  notification,
  onSelect,
}: {
  notification: Notification;
  onSelect: (notification: Notification) => void;
}) {
  const time = formatRelativeTime(notification.created_at);

  return (
    <button
      type="button"
      onClick={() => onSelect(notification)}
      className="flex w-full cursor-pointer gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-muted"
    >
      {/* Unread dot — the quiet, per-item signal. */}
      <span className="flex w-2 flex-none justify-center pt-1.5">
        {!notification.is_read && (
          <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={`block truncate text-sm ${
            notification.is_read ? "font-normal text-muted-foreground" : "font-medium text-foreground"
          }`}
        >
          {notification.title}
        </span>
        {notification.message && (
          <span
            className={`mt-0.5 block text-xs leading-5 ${
              notification.is_read ? "text-muted-foreground/80" : "text-muted-foreground"
            }`}
          >
            {notification.message}
          </span>
        )}
        {time && (
          <span className="mt-1 block text-xs text-muted-foreground/70">{time}</span>
        )}
      </span>
    </button>
  );
}

export function NotificationBell() {
  const { token, loading } = useAuth();
  const router = useRouter();

  const [unreadCount, setUnreadCount] = useState(0);
  // Which token the count belongs to — lets the badge derive to 0 on logout/
  // user switch without resetting state inside the poll effect (lint rule:
  // no setState directly in an effect body).
  const [countedToken, setCountedToken] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<Notification[] | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Viewport coords for the fixed panel, captured from the bell rect on open.
  // `bottom` is set instead of `top` when the panel opens upward.
  const [panelPos, setPanelPos] = useState<{
    left: number;
    top?: number;
    bottom?: number;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const refreshUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const count = await getUnreadCount(token);
      setUnreadCount(count);
      setCountedToken(token);
    } catch {
      // Polling failures stay silent — a failed count fetch shouldn't nag the
      // user; the badge just refreshes on the next tick or dropdown open.
    }
  }, [token]);

  // Poll the unread count while authenticated; skip entirely when signed out.
  // The first fetch is scheduled, not run inline, so the effect body itself
  // never sets state (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!token) return;

    const first = setTimeout(refreshUnreadCount, 0);
    const interval = setInterval(refreshUnreadCount, UNREAD_POLL_MS);
    return () => {
      clearTimeout(first);
      clearInterval(interval);
    };
  }, [token, refreshUnreadCount]);

  const loadNotifications = useCallback(async () => {
    if (!token) return;
    setLoadingList(true);
    setError(null);
    try {
      const list = await getNotifications(token, { limit: DROPDOWN_LIMIT });
      setNotifications(list);
      // The badge reflects the count endpoint; the list itself stays as
      // returned (unread_only=false by design, so read ones stay visible).
      setCountedToken(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load your notifications.");
    } finally {
      setLoadingList(false);
    }
  }, [token]);

  const openDropdown = useCallback(() => {
    // Anchor the fixed panel to the bell's current viewport rect at click
    // time, clamped to both viewport axes. The panel height varies with its
    // content (up to max-h-96 = 384px + header), so vertical placement uses a
    // worst-case estimate rather than a measured height.
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const PANEL_WIDTH = 320; // w-80
      const PANEL_MAX_HEIGHT = 400; // max-h-96 list + header row + border
      const GAP = 8;
      const margin = 8;

      // Horizontal: prefer left-aligned with the bell; flip to right-aligned
      // near the viewport edge (mobile top bar) and clamp into view.
      let left = rect.left;
      if (left + PANEL_WIDTH + margin > window.innerWidth) {
        left = window.innerWidth - PANEL_WIDTH - margin;
      }
      left = Math.max(margin, left);

      // Vertical: prefer opening downward. If the space below can't fit the
      // worst-case panel and there's more room above, open upward — anchored
      // by CSS `bottom` (viewport bottom edge of the panel fixed to the
      // button's top edge minus the gap), so the variable-height panel grows
      // upward and never runs past the bottom of the viewport.
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < PANEL_MAX_HEIGHT + GAP && spaceAbove > spaceBelow;

      setPanelPos(
        openUpward
          ? { left, bottom: window.innerHeight - rect.top + GAP }
          : { left, top: rect.bottom + GAP }
      );
    }
    setOpen(true);
    loadNotifications();
  }, [loadNotifications]);

  const closeDropdown = useCallback(() => setOpen(false), []);

  // Scroll or resize while open → close, so the panel can't drift away from
  // its bell anchor.
  useEffect(() => {
    if (!open) return;

    window.addEventListener("scroll", closeDropdown, true);
    window.addEventListener("resize", closeDropdown);
    return () => {
      window.removeEventListener("scroll", closeDropdown, true);
      window.removeEventListener("resize", closeDropdown);
    };
  }, [open, closeDropdown]);

  // Click-outside + Escape to close.
  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  async function handleSelect(notification: Notification) {
    if (!token) return;

    // Optimistically mark read so the dot/badge update without a spinner.
    setNotifications((current) =>
      current
        ? current.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
        : current
    );
    setUnreadCount((current) => Math.max(0, current - (notification.is_read ? 0 : 1)));

    if (!notification.is_read) {
      try {
        await markNotificationRead(notification.id, token);
      } catch {
        // Non-fatal: the badge refreshes on the next poll tick anyway.
      }
    }

    if (notification.job_id !== null) {
      setOpen(false);
      router.push(`/jobs/${notification.job_id}`);
    }
  }

  async function handleMarkAllRead() {
    if (!token) return;

    setNotifications((current) =>
      current ? current.map((n) => ({ ...n, is_read: true })) : current
    );
    setUnreadCount(0);

    try {
      await markAllNotificationsRead(token);
    } catch {
      // Non-fatal: next poll reconciles the badge.
    }
  }

  // Signed-out users (and the initial auth restore) get nothing at all — the
  // endpoints are auth-protected, so an anonymous bell would only misfire.
  if (loading || !token) return null;

  // The count only counts for the session that fetched it; a fresh login (or
  // a stale fetch from a previous token) shows no badge until it polls.
  const activeUnreadCount = countedToken === token ? unreadCount : 0;
  const badgeLabel = activeUnreadCount > 9 ? "9+" : String(activeUnreadCount);

  // self-start/h-fit keep the wrapper from stretching inside flex parents;
  // the panel itself is fixed, so the wrapper needs no positioning context.
  return (
    <div ref={containerRef} className="relative h-fit self-start">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (open ? closeDropdown() : openDropdown())}
        aria-label={
          activeUnreadCount > 0
            ? `Notifications, ${activeUnreadCount} unread`
            : "Notifications"
        }
        aria-expanded={open}
        title="Notifications"
        className="relative flex h-10 w-10 flex-none cursor-pointer items-center justify-center rounded-md bg-muted text-muted-foreground transition-colors hover:text-foreground"
      >
        <BellIcon className="h-5 w-5" />
        {activeUnreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-semibold leading-none text-destructive-foreground">
            {badgeLabel}
          </span>
        )}
      </button>

      {open && panelPos && (
        <div
          role="dialog"
          aria-label="Notifications"
          style={{
            left: panelPos.left,
            top: panelPos.top,
            bottom: panelPos.bottom,
          }}
          className="fixed z-30 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-border bg-card shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <p className="font-heading text-sm font-semibold text-foreground">Notifications</p>
            {activeUnreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="cursor-pointer rounded-md text-xs font-medium text-primary transition-colors hover:text-primary-hover"
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto p-1.5">
            {loadingList && notifications === null ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                <SpinnerIcon className="h-4 w-4" />
                Loading…
              </div>
            ) : error ? (
              <p className="px-3 py-8 text-center text-sm text-destructive">{error}</p>
            ) : notifications && notifications.length > 0 ? (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onSelect={handleSelect}
                />
              ))
            ) : (
              <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
                <InboxIcon className="h-6 w-6 text-muted-foreground/60" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
